import { v4 as uuidv4 } from 'uuid';

/**
 * FrameRun - Analytics Tracker Script
 * Parte do sistema Visioneta
 */
class FrameRun {
  constructor() {
    this.config = {
      endpoint: 'https://nexus.visioneta.com.br/collect',
      sessionKey: 'visioneta_session_id',
      retryAttempts: 3,
      retryDelay: 1000, // 1 segundo
      // Configurações de subdomain tracking
      allowSubdomains: false, // Será configurado via data attribute
      allowedSubdomains: [] // Lista de subdomínios autorizados
    };
    
    this.siteId = null;
    this.sessionId = null;
    this.isInitialized = false;
    
    this.init();
  }

  /**
   * Inicializa o tracker
   */
  init() {
    try {
      // Obtém o site ID do data attribute
      this.siteId = this.getSiteId();
      
      if (!this.siteId) {
        console.warn('[FrameRun] Site ID não encontrado. Adicione data-site-id ao script.');
        return;
      }

      // Obtém ou cria session ID
      this.sessionId = this.getOrCreateSessionId();
      
      // Marca como inicializado
      this.isInitialized = true;
      
      // Tenta reenviar requisições falhas anteriores
      this.retryFailedRequests();
      
      // Coleta e envia dados da visita
      this.trackPageView();
      
      console.log('[FrameRun] Inicializado com sucesso para site:', this.siteId);
      
    } catch (error) {
      console.error('[FrameRun] Erro na inicialização:', error);
    }
  }

  /**
   * Obtém o site ID e configurações do data attribute do script
   */
  getSiteId() {
    // Prioridade 1: Configuração via window.FRAMERUN_CONFIG (para GTM)
    if (window.FRAMERUN_CONFIG && window.FRAMERUN_CONFIG.siteId) {
      const config = window.FRAMERUN_CONFIG;
      this.config.allowSubdomains = config.allowSubdomains || false;
      this.config.allowedSubdomains = config.allowedSubdomains || [];
      return config.siteId;
    }
    
    // Prioridade 2: Data attributes no script tag
    const scripts = document.getElementsByTagName('script');
    
    for (const script of scripts) {
      if (script.src && script.src.includes('framerun.js')) {
        // Obtém configurações de subdomain tracking
        const allowSubdomains = script.getAttribute('data-allow-subdomains') === 'true';
        const allowedSubdomains = script.getAttribute('data-allowed-subdomains');
        
        this.config.allowSubdomains = allowSubdomains;
        if (allowedSubdomains) {
          this.config.allowedSubdomains = allowedSubdomains.split(',').map(s => s.trim());
        }
        
        return script.getAttribute('data-site-id');
      }
    }
    
    return null;
  }

  /**
   * Obtém session ID existente ou cria um novo
   */
  getOrCreateSessionId() {
    try {
      let sessionId = localStorage.getItem(this.config.sessionKey);
      
      if (!sessionId) {
        sessionId = uuidv4();
        localStorage.setItem(this.config.sessionKey, sessionId);
      }
      
      return sessionId;
    } catch (error) {
      // Fallback se localStorage não estiver disponível
      console.warn('[FrameRun] localStorage não disponível, usando session temporária');
      return uuidv4();
    }
  }

  /**
   * Coleta dados da visita atual
   */
  collectVisitData() {
    const utmParams = this.extractUtmParameters();
    const trafficSource = this.identifyTrafficSource();
    const deviceInfo = this.collectDeviceInfo();
    
    const data = {
      siteId: this.siteId,
      url: window.location.href,
      referer: document.referrer || '',
      userAgent: navigator.userAgent,
      language: navigator.language || navigator.browserLanguage || '',
      timezone: this.getTimezone(),
      timestamp: Date.now(),
      sessionId: this.sessionId,
      // Dados do dispositivo (coletados uma vez por sessão)
      device: deviceInfo,
      // Dados de atribuição/origem
      traffic: {
        source: trafficSource.source,
        medium: trafficSource.medium,
        campaign: utmParams.campaign,
        term: utmParams.term,
        content: utmParams.content,
        isReturning: this.isReturningVisitor()
      }
    };

    return data;
  }

  /**
   * Coleta informações detalhadas do dispositivo (uma vez por sessão)
   */
  collectDeviceInfo() {
    const device = {
      screen: {
        width: screen.width,
        height: screen.height,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth,
        orientation: this.getScreenOrientation()
      },
      viewport: {
        width: (typeof window !== 'undefined' && window.innerWidth) || 
               (typeof document !== 'undefined' && document.documentElement && document.documentElement.clientWidth) || 
               1024,
        height: (typeof window !== 'undefined' && window.innerHeight) || 
                (typeof document !== 'undefined' && document.documentElement && document.documentElement.clientHeight) || 
                768
      },
      // Informações do navegador
      browser: this.getBrowserInfo(),
      // Informações do OS
      os: this.getOSInfo(),
      // Informações de hardware (quando disponível)
      hardware: this.getHardwareInfo(),
      // Suporte a recursos
      features: {
        localStorage: this.hasLocalStorage(),
        sessionStorage: this.hasSessionStorage(),
        cookies: navigator.cookieEnabled,
        javaEnabled: navigator.javaEnabled ? navigator.javaEnabled() : false,
        onLine: navigator.onLine,
        touchSupport: this.hasTouchSupport(),
        webGL: this.hasWebGL(),
        webRTC: this.hasWebRTC(),
        serviceWorker: this.hasServiceWorker(),
        darkMode: this.isDarkMode()
      },
      // Informações de rede (se disponível)
      connection: this.getConnectionInfo(),
      // Performance da página
      performance: this.getPerformanceInfo()
    };

    return device;
  }

  /**
   * Identifica informações do navegador
   */
  getBrowserInfo() {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let version = 'Unknown';

    // Chrome
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      browser = 'Chrome';
      const match = ua.match(/Chrome\/([0-9.]+)/);
      version = match ? match[1] : 'Unknown';
    }
    // Firefox
    else if (ua.includes('Firefox')) {
      browser = 'Firefox';
      const match = ua.match(/Firefox\/([0-9.]+)/);
      version = match ? match[1] : 'Unknown';
    }
    // Safari
    else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      browser = 'Safari';
      const match = ua.match(/Version\/([0-9.]+)/);
      version = match ? match[1] : 'Unknown';
    }
    // Edge
    else if (ua.includes('Edg')) {
      browser = 'Edge';
      const match = ua.match(/Edg\/([0-9.]+)/);
      version = match ? match[1] : 'Unknown';
    }

    return {
      name: browser,
      version: version,
      platform: navigator.platform || 'Unknown'
    };
  }

  /**
   * Identifica informações do sistema operacional
   */
  getOSInfo() {
    const ua = navigator.userAgent;
    let os = 'Unknown';

    if (ua.includes('Windows NT')) {
      os = 'Windows';
    } else if (ua.includes('Mac OS X')) {
      os = 'macOS';
    } else if (ua.includes('Linux')) {
      os = 'Linux';
    } else if (ua.includes('Android')) {
      os = 'Android';
    } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
      os = 'iOS';
    }

    return {
      name: os,
      platform: navigator.platform || 'Unknown'
    };
  }

  /**
   * Verifica suporte a localStorage
   */
  hasLocalStorage() {
    try {
      const test = '__test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Verifica suporte a sessionStorage
   */
  hasSessionStorage() {
    try {
      const test = '__test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Verifica suporte a touch
   */
  hasTouchSupport() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Verifica suporte a WebGL
   */
  hasWebGL() {
    try {
      if (typeof document === 'undefined') return false;
      
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    } catch (e) {
      return false;
    }
  }

  /**
   * Obtém informações de conexão (se disponível)
   */
  getConnectionInfo() {
    if (navigator.connection || navigator.mozConnection || navigator.webkitConnection) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      return {
        effectiveType: connection.effectiveType || 'Unknown',
        downlink: connection.downlink || null,
        rtt: connection.rtt || null,
        saveData: connection.saveData || false
      };
    }
    
    return null;
  }

  /**
   * Extrai parâmetros UTM da URL
   */
  extractUtmParameters() {
    const url = new URL(window.location.href);
    
    return {
      source: url.searchParams.get('utm_source') || '',
      medium: url.searchParams.get('utm_medium') || '',
      campaign: url.searchParams.get('utm_campaign') || '',
      term: url.searchParams.get('utm_term') || '',
      content: url.searchParams.get('utm_content') || ''
    };
  }

  /**
   * Identifica a fonte de tráfego
   */
  identifyTrafficSource() {
    const url = new URL(window.location.href);
    const referer = document.referrer;
    
    // UTM parameters têm prioridade
    const utmSource = url.searchParams.get('utm_source');
    const utmMedium = url.searchParams.get('utm_medium');
    
    if (utmSource && utmMedium) {
      return {
        source: utmSource,
        medium: utmMedium
      };
    }
    
    // Se não há referrer, é tráfego direto
    if (!referer) {
      return {
        source: 'direct',
        medium: 'none'
      };
    }
    
    try {
      const refererUrl = new URL(referer);
      const refererDomain = refererUrl.hostname.toLowerCase();
      const currentDomain = window.location.hostname.toLowerCase();
      
      // Tráfego interno (mesmo domínio ou subdomínio autorizado)
      if (this.isSameDomainOrAuthorizedSubdomain(refererDomain, currentDomain)) {
        return {
          source: 'internal',
          medium: 'referral'
        };
      }
      
      // Mecanismos de busca conhecidos
      const searchEngines = {
        'google.': 'google',
        'bing.': 'bing',
        'yahoo.': 'yahoo',
        'duckduckgo.': 'duckduckgo',
        'baidu.': 'baidu',
        'yandex.': 'yandex'
      };
      
      for (const [domain, engine] of Object.entries(searchEngines)) {
        if (refererDomain.includes(domain)) {
          return {
            source: engine,
            medium: 'organic'
          };
        }
      }
      
      // Redes sociais conhecidas
      const socialNetworks = {
        'facebook.': 'facebook',
        'instagram.': 'instagram',
        'twitter.': 'twitter',
        'linkedin.': 'linkedin',
        'youtube.': 'youtube',
        'tiktok.': 'tiktok',
        'whatsapp.': 'whatsapp',
        't.co': 'twitter'
      };
      
      for (const [domain, network] of Object.entries(socialNetworks)) {
        if (refererDomain.includes(domain)) {
          return {
            source: network,
            medium: 'social'
          };
        }
      }
      
      // Qualquer outro referrer externo
      return {
        source: refererDomain,
        medium: 'referral'
      };
      
    } catch (error) {
      // Se houver erro ao processar o referrer
      return {
        source: 'unknown',
        medium: 'referral'
      };
    }
  }

  /**
   * Verifica se é um visitante retornando
   */
  isReturningVisitor() {
    try {
      const visitKey = `${this.config.sessionKey}_first_visit`;
      const firstVisit = localStorage.getItem(visitKey);
      
      if (!firstVisit) {
        // Primeira visita - marca timestamp
        localStorage.setItem(visitKey, Date.now().toString());
        return false;
      }
      
      return true;
    } catch (error) {
      // Se localStorage não estiver disponível, assume como nova visita
      return false;
    }
  }

  /**
   * Obtém o timezone do usuário
   */
  getTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      return 'Unknown';
    }
  }

  /**
   * Rastreia uma visualização de página
   */
  trackPageView() {
    if (!this.isInitialized) {
      console.warn('[FrameRun] Tracker não inicializado');
      return;
    }

    const data = this.collectVisitData();
    this.sendData(data);
  }

  /**
   * Envia dados para o endpoint com retry e compressão
   */
  async sendData(data, attempt = 1) {
    try {
      const payload = JSON.stringify(data);
      
      // Headers para envio
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'User-Agent': navigator.userAgent
      };

      // Compressão será feita pelo CloudFlare automaticamente
      // se o payload for > 1KB e o cliente suportar gzip
      if (payload.length > 1024) {
        headers['Accept-Encoding'] = 'gzip, deflate';
      }

      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: headers,
        body: payload,
        // Configurações importantes para analytics
        keepalive: true, // Permite envio mesmo se a página estiver sendo fechada
        credentials: 'omit', // Não envia cookies para reduzir overhead
        // Timeout implícito via AbortController se necessário
        signal: this.createTimeoutSignal(10000) // 10 segundos
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('[FrameRun] Dados enviados com sucesso');
      
    } catch (error) {
      console.error(`[FrameRun] Erro no envio (tentativa ${attempt}):`, error);
      
      // Retry logic com exponential backoff
      if (attempt < this.config.retryAttempts) {
        const delay = this.config.retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
        console.log(`[FrameRun] Tentando novamente em ${delay}ms...`);
        
        setTimeout(() => {
          this.sendData(data, attempt + 1);
        }, delay);
      } else {
        console.error('[FrameRun] Falha definitiva no envio dos dados');
        // Opcionalmente, salvar no localStorage para retry posterior
        this.saveFailedRequest(data);
      }
    }
  }

  /**
   * Cria um signal para timeout de requisições
   */
  createTimeoutSignal(timeout) {
    if (typeof AbortController !== 'undefined') {
      const controller = new AbortController();
      setTimeout(() => controller.abort(), timeout);
      return controller.signal;
    }
    return undefined;
  }

  /**
   * Salva requisição falha para retry posterior
   */
  saveFailedRequest(data) {
    try {
      const failedRequests = JSON.parse(localStorage.getItem('framerun_failed_requests') || '[]');
      failedRequests.push({
        data,
        timestamp: Date.now()
      });
      
      // Mantém apenas as últimas 10 requisições falhas
      if (failedRequests.length > 10) {
        failedRequests.splice(0, failedRequests.length - 10);
      }
      
      localStorage.setItem('framerun_failed_requests', JSON.stringify(failedRequests));
    } catch (error) {
      console.warn('[FrameRun] Não foi possível salvar requisição falha:', error);
    }
  }

  /**
   * Tenta reenviar requisições falhas salvas
   */
  retryFailedRequests() {
    try {
      const failedRequests = JSON.parse(localStorage.getItem('framerun_failed_requests') || '[]');
      
      if (failedRequests.length > 0) {
        console.log(`[FrameRun] Reenviando ${failedRequests.length} requisições falhas...`);
        
        failedRequests.forEach(({ data }) => {
          this.sendData(data);
        });
        
        // Limpa as requisições após tentar reenviar
        localStorage.removeItem('framerun_failed_requests');
      }
    } catch (error) {
      console.warn('[FrameRun] Erro ao reenviar requisições falhas:', error);
    }
  }

  /**
   * Verifica se é o mesmo domínio ou subdomínio autorizado
   */
  isSameDomainOrAuthorizedSubdomain(refererDomain, currentDomain) {
    // Mesmo domínio exato
    if (refererDomain === currentDomain) {
      return true;
    }

    // Se não permite subdomínios, retorna false
    if (!this.config.allowSubdomains) {
      return false;
    }

    // Verifica se é um subdomínio autorizado
    if (this.config.allowedSubdomains.length > 0) {
      return this.config.allowedSubdomains.some(subdomain => 
        refererDomain === subdomain || currentDomain === subdomain
      );
    }

    // Se permite subdomínios mas não tem lista específica, 
    // verifica se são do mesmo domínio raiz
    const refererParts = refererDomain.split('.');
    const currentParts = currentDomain.split('.');

    if (refererParts.length >= 2 && currentParts.length >= 2) {
      const refererRoot = refererParts.slice(-2).join('.');
      const currentRoot = currentParts.slice(-2).join('.');
      return refererRoot === currentRoot;
    }

    return false;
  }



  /**
   * Método público para rastrear eventos customizados (futuro)
   */
  track(eventName, eventData = {}) {
    if (!this.isInitialized) {
      console.warn('[FrameRun] Tracker não inicializado');
      return;
    }

    const data = {
      ...this.collectVisitData(),
      eventName,
      eventData
    };

    this.sendData(data);
  }

  /**
   * Obtém informações de hardware (quando disponível)
   */
  getHardwareInfo() {
    const hardware = {
      cpuCores: navigator.hardwareConcurrency || null,
      deviceMemory: navigator.deviceMemory || null,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      platform: navigator.platform || 'Unknown'
    };

    // Informações de tela para categorizar dispositivo
    const screenSize = screen.width * screen.height;
    let deviceType = 'unknown';
    
    if (screenSize < 500000) { // ~707x707
      deviceType = 'mobile';
    } else if (screenSize < 1000000) { // ~1000x1000  
      deviceType = 'tablet';
    } else {
      deviceType = 'desktop';
    }

    hardware.deviceType = deviceType;
    
    return hardware;
  }

  /**
   * Obtém orientação da tela
   */
  getScreenOrientation() {
    if (screen.orientation) {
      return {
        type: screen.orientation.type,
        angle: screen.orientation.angle
      };
    }
    
    // Fallback baseado em dimensões
    const orientation = screen.width > screen.height ? 'landscape' : 'portrait';
    return {
      type: orientation,
      angle: 0
    };
  }

  /**
   * Verifica suporte a WebRTC
   */
  hasWebRTC() {
    return !!(window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection);
  }

  /**
   * Verifica suporte a Service Worker
   */
  hasServiceWorker() {
    return 'serviceWorker' in navigator;
  }

  /**
   * Detecta se está em modo escuro
   */
  isDarkMode() {
    if (window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  }

  /**
   * Obtém informações de performance da página
   */
  getPerformanceInfo() {
    if (!window.performance || !window.performance.timing) {
      return null;
    }

    const timing = window.performance.timing;
    const navigation = window.performance.navigation;
    
    const performance = {
      // Tempo total de carregamento da página
      loadTime: timing.loadEventEnd - timing.navigationStart,
      // Tempo até o DOM estar pronto
      domReady: timing.domContentLoadedEventEnd - timing.navigationStart,
      // Tempo de conexão
      connectTime: timing.connectEnd - timing.connectStart,
      // Tempo de resposta do servidor
      responseTime: timing.responseEnd - timing.requestStart,
      // Tipo de navegação (0=navegação, 1=reload, 2=back/forward)
      navigationType: navigation.type,
      // Número de redirecionamentos
      redirectCount: navigation.redirectCount
    };

    // Performance API mais moderna (se disponível)
    if (window.performance.getEntriesByType) {
      const paintEntries = window.performance.getEntriesByType('paint');
      
      paintEntries.forEach(entry => {
        if (entry.name === 'first-paint') {
          performance.firstPaint = Math.round(entry.startTime);
        }
        if (entry.name === 'first-contentful-paint') {
          performance.firstContentfulPaint = Math.round(entry.startTime);
        }
      });
    }

    return performance;
  }
}

// Auto-inicialização quando o DOM estiver pronto
function initFrameRun() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new FrameRun();
    });
  } else {
    new FrameRun();
  }
}

// Executa a inicialização
initFrameRun();

// Exporta para uso programático (opcional)
export default FrameRun;
